import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

import type { CenterAppointment } from "../../../center";
import MonthAgendaGrid from "../../../../shared/ui/calendar/MonthAgendaGrid";
import { bookingCardStyle } from "../../../../shared/ui/calendar/bookingCardStyle";
import {
    layoutDayEvents,
    visibleHourRange,
    WeekSpan,
} from "../../../../shared/domain/weekLayout";
import {
    AgendaCalendarView,
    bookingsByDay,
    calendarRange,
    initialsOf,
    isBookingPast,
    minuteLabel,
    minutesOfTime,
    overlappingCardBox,
    shiftAnchor,
    weekDays,
} from "../../domain/agendaCalendar";

interface AgendaCalendarProps {
    view: AgendaCalendarView;
    /** Any day of the week or month on screen, `YYYY-MM-DD`. */
    anchor: string;
    onAnchorChange: (anchor: string) => void;
    /** Opens the week of a day, from a month cell with more than it can show. */
    onShowWeek: (day: string) => void;
    appointments: readonly CenterAppointment[];
    therapistName: (therapistId: string) => string;
    colorOf: (therapistId: string) => string;
    /** Who the booking is with, as the list writes it. */
    subjectOf: (appointment: CenterAppointment) => string;
    todayKey: string;
    /** Minutes since midnight now, so today's finished bookings fade too. */
    nowMinute: number;
    onOpen: (appointment: CenterAppointment) => void;
}

/** One hour of the week grid, in pixels. */
const HOUR_HEIGHT = 52;
/** A card this tall fits a second line: the therapist under the time and who. */
const TWO_LINE_MIN_HEIGHT = 38;

function appointmentKey(appointment: CenterAppointment): string {
    return `${appointment.appointment_id ?? appointment.session_id ?? ""}-${appointment.date}-${appointment.hour}`;
}

function isCancelled(appointment: CenterAppointment): boolean {
    return appointment.status === "cancelled" || appointment.session_status === "cancelled";
}

/**
 * The centre's agenda as a calendar, every therapist in their own colour.
 *
 * Bookings of different therapists at the same time overlap in the day's
 * column, each in its own lane and stretching over the next, as Google
 * Calendar draws them, so every one stays visible and keeps room for a name.
 */
export default function AgendaCalendar(props: AgendaCalendarProps) {
    const { t, i18n } = useTranslation();
    const { view, anchor, onAnchorChange, todayKey } = props;
    const { from, to } = calendarRange(view, anchor);

    const title =
        view === "month"
            ? new Date(`${from}T12:00:00`).toLocaleDateString(i18n.language, {
                  month: "long",
                  year: "numeric",
              })
            : `${new Date(`${from}T12:00:00`).toLocaleDateString(i18n.language, {
                  day: "numeric",
                  month: "short",
              })} – ${new Date(`${to}T12:00:00`).toLocaleDateString(i18n.language, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
              })}`;

    return (
        <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-content-primary first-letter:uppercase">
                    {title}
                </h3>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => onAnchorChange(todayKey)}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-content-secondary transition-colors hover:bg-gray-100 dark:border-white/10 dark:hover:bg-white/5"
                    >
                        {t("center.agenda.calendar.today")}
                    </button>
                    <button
                        type="button"
                        aria-label={t(`center.agenda.calendar.previous_${view}`)}
                        onClick={() => onAnchorChange(shiftAnchor(view, anchor, -1))}
                        className="rounded-lg p-2 text-content-tertiary transition-colors hover:bg-gray-100 dark:hover:bg-white/5"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        aria-label={t(`center.agenda.calendar.next_${view}`)}
                        onClick={() => onAnchorChange(shiftAnchor(view, anchor, 1))}
                        className="rounded-lg p-2 text-content-tertiary transition-colors hover:bg-gray-100 dark:hover:bg-white/5"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {view === "week" ? <WeekView {...props} /> : <MonthView {...props} />}
        </div>
    );
}

function WeekView({
    anchor,
    appointments,
    therapistName,
    colorOf,
    subjectOf,
    todayKey,
    nowMinute,
    onOpen,
}: AgendaCalendarProps) {
    const { t, i18n } = useTranslation();
    const days = weekDays(calendarRange("week", anchor).from);
    const byDay = useMemo(() => bookingsByDay(appointments), [appointments]);

    const spanOf = (appointment: CenterAppointment): WeekSpan => {
        const startMinute = minutesOfTime(appointment.hour);
        return { startMinute, endMinute: startMinute + appointment.duration };
    };

    const { startHour, endHour } = visibleHourRange(appointments.map(spanOf));
    const gridStartMinute = startHour * 60;
    const totalMinutes = (endHour - startHour) * 60;
    const gridHeight = (endHour - startHour) * HOUR_HEIGHT;
    const offsetOf = (minute: number) =>
        ((Math.min(Math.max(minute, gridStartMinute), gridStartMinute + totalMinutes) -
            gridStartMinute) /
            totalMinutes) *
        gridHeight;
    const hours = Array.from({ length: endHour - startHour }, (_, index) => startHour + index);
    const columns = "grid-cols-[3.25rem_repeat(7,minmax(0,1fr))]";

    return (
        <div className="max-h-[70vh] overflow-auto rounded-xl border border-gray-200 dark:border-white/10">
            <div className="min-w-[640px]">
                <div
                    className={`sticky top-0 z-20 grid ${columns} border-b border-gray-200 bg-white/90 backdrop-blur dark:border-white/10 dark:bg-gray-900/90`}
                >
                    <div />
                    {days.map((day) => (
                        <div
                            key={day}
                            className="flex flex-col items-center gap-0.5 border-l border-gray-100 py-2 dark:border-white/5"
                        >
                            <span className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
                                {new Date(`${day}T12:00:00`).toLocaleDateString(i18n.language, {
                                    weekday: "short",
                                })}
                            </span>
                            <span
                                className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                                    day === todayKey
                                        ? "bg-brand-600 text-white"
                                        : "text-gray-700 dark:text-gray-200"
                                }`}
                            >
                                {Number(day.slice(8, 10))}
                            </span>
                        </div>
                    ))}
                </div>

                <div className={`grid ${columns}`}>
                    <div className="relative" style={{ height: gridHeight }}>
                        {hours.map((hour, index) =>
                            index === 0 ? null : (
                                <div
                                    key={hour}
                                    className="absolute right-2 -translate-y-1/2 text-xs tabular-nums text-content-tertiary"
                                    style={{ top: index * HOUR_HEIGHT }}
                                >
                                    {`${`${hour}`.padStart(2, "0")}:00`}
                                </div>
                            )
                        )}
                    </div>

                    {days.map((day) => {
                        const dayAppointments = byDay.get(day) ?? [];
                        const spans = dayAppointments.map(spanOf);
                        const placements = layoutDayEvents(spans);

                        return (
                            <div
                                key={day}
                                data-day={day}
                                className="relative border-l border-gray-100 dark:border-white/5"
                                style={{ height: gridHeight }}
                            >
                                {hours.map((hour, index) => (
                                    <div
                                        key={hour}
                                        aria-hidden="true"
                                        className="absolute inset-x-0 border-t border-gray-100 dark:border-white/5"
                                        style={{ top: index * HOUR_HEIGHT }}
                                    />
                                ))}

                                {day === todayKey && (
                                    <div
                                        aria-hidden="true"
                                        className="absolute inset-x-0 z-10 border-t-2 border-red-500"
                                        style={{ top: offsetOf(nowMinute) }}
                                    />
                                )}

                                {dayAppointments.map((appointment, index) => {
                                    const placement = placements[index];
                                    const span = spans[index];
                                    const top = offsetOf(span.startMinute);
                                    const height = Math.max(offsetOf(span.endMinute) - top, 18);
                                    const box = overlappingCardBox(placement.lane, placement.lanes);
                                    const past = isBookingPast(appointment, todayKey, nowMinute);
                                    const therapist = therapistName(appointment.therapist_id);
                                    const subject = subjectOf(appointment);
                                    const time = `${minuteLabel(span.startMinute)} – ${minuteLabel(span.endMinute)}`;
                                    // Sharing the column, a card is too narrow
                                    // for a whole name; the initials still say whose.
                                    const therapistLabel =
                                        placement.lanes > 2 ? initialsOf(therapist) : therapist;

                                    return (
                                        <button
                                            key={appointmentKey(appointment)}
                                            type="button"
                                            data-past={past || undefined}
                                            data-therapist={appointment.therapist_id}
                                            onClick={() => onOpen(appointment)}
                                            title={`${time} · ${subject} · ${therapist}`}
                                            aria-label={t("center.agenda.calendar.openBooking", {
                                                time,
                                                subject,
                                                therapist,
                                            })}
                                            className={`absolute overflow-hidden rounded-md border-l-4 bg-white px-1.5 py-1 text-left shadow-sm ring-1 ring-white transition-[filter] hover:brightness-95 dark:bg-gray-900 dark:ring-gray-900 ${
                                                past ? "text-content-tertiary" : "text-content-primary"
                                            } ${isCancelled(appointment) ? "line-through" : ""}`}
                                            style={{
                                                ...bookingCardStyle(colorOf(appointment.therapist_id), past),
                                                top,
                                                height,
                                                left: `calc(${box.left}% + 2px)`,
                                                width: `calc(${box.width}% - 4px)`,
                                                // Each lane over the one before it.
                                                zIndex: 10 + placement.lane,
                                            }}
                                        >
                                            <p className="truncate text-xs font-semibold leading-tight">
                                                <span className="tabular-nums">{appointment.hour.slice(0, 5)}</span>{" "}
                                                {subject}
                                            </p>
                                            {height >= TWO_LINE_MIN_HEIGHT && (
                                                <p className="truncate text-xs leading-tight opacity-90">
                                                    {therapistLabel}
                                                </p>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function MonthView({
    anchor,
    appointments,
    therapistName,
    colorOf,
    subjectOf,
    todayKey,
    nowMinute,
    onOpen,
    onShowWeek,
}: AgendaCalendarProps) {
    const { t } = useTranslation();

    return (
        <MonthAgendaGrid
            anchor={anchor}
            todayKey={todayKey}
            bookings={appointments}
            cardOf={(appointment) => {
                const therapist = therapistName(appointment.therapist_id);
                const subject = subjectOf(appointment);
                const start = minutesOfTime(appointment.hour);
                const time = `${minuteLabel(start)} – ${minuteLabel(start + appointment.duration)}`;
                return {
                    key: appointmentKey(appointment),
                    text: `${initialsOf(therapist)} · ${subject}`,
                    title: `${time} · ${subject} · ${therapist}`,
                    ariaLabel: t("center.agenda.calendar.openBooking", { time, subject, therapist }),
                    color: colorOf(appointment.therapist_id),
                    past: isBookingPast(appointment, todayKey, nowMinute),
                    cancelled: isCancelled(appointment),
                };
            }}
            onOpen={onOpen}
            onShowMore={onShowWeek}
            moreLabel={(count) => t("center.agenda.calendar.more", { count })}
        />
    );
}
